// src/customer/Customers.jsx
import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import AppModal from "../components/AppModal";
import CustomDataTable from "../components/DataTable";
import { FiEdit, FiTrash2, FiEye } from "react-icons/fi";
import "./Customers.css";

export default function Customers({ darkMode }) {
    const [show, setShow] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const columns = [
        {
            name: "Name",
            selector: row => row.name || "-",
            sortable: true,
            cell: row => <span className="customer-name">{row.name || "-"}</span>,
        },
        {
            name: "Email",
            selector: row => row.email || "-",
            sortable: true,
            cell: row => <span className="customer-email">{row.email || "-"}</span>,
        },
        {
            name: "Subscription Status",
            cell: row => (
                <span
                    className={row.subscription_status === "active" 
                        ? "status-badge active" 
                        : "status-badge inactive"
                    }
                >
                    {row.subscription_label || (row.subscription_status === "active" ? "Active" : "Inactive")}
                </span>
            ),
        },
        {
            name: "Status",
            cell: row => (
                <span
                    className={row.status === "active" 
                        ? "status-badge active" 
                        : "status-badge inactive"
                    }
                >
                    {row.status === "active" ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: "Actions",
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="action-btn view" 
                        onClick={() => handleView(row)}
                        title="View Customer"
                    >
                        <FiEye size={14} />
                    </button>
                    <button 
                        className="action-btn edit" 
                        onClick={() => handleEdit(row)}
                        title="Edit Customer"
                    >
                        <FiEdit size={14} />
                    </button>
                    <button 
                        className="action-btn delete" 
                        onClick={() => handleDelete(row._id)}
                        title="Delete Customer"
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    // Fetch customers from API
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                const baseUrl = process.env.REACT_APP_ENV === 'local' 
                    ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                    : process.env.REACT_APP_BACKEND_USER_API;
                
                const res = await fetch(`${baseUrl}/api/users/registered-users`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
                    },
                });
                
                if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
                const json = await res.json();
                setCustomers(Array.isArray(json.data) ? json.data : []);
            } catch (err) {
                console.error('Failed to fetch customers', err);
                setCustomers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    // Handle view customer
    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setShowViewModal(true);
    };

    // Handle edit customer
    const handleEdit = (customer) => {
        console.log('Edit customer:', customer);
        setSelectedCustomer(customer);
        setShow(true);
    };

    // Handle delete customer
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this customer?')) return;
        
        try {
            const baseUrl = process.env.REACT_APP_ENV === 'local' 
                ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
                : process.env.REACT_APP_BACKEND_USER_API;
            
            const res = await fetch(`${baseUrl}/api/users/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
                },
            });
            
            if (!res.ok) throw new Error(`Delete failed ${res.status}`);
            
            // Refresh customer list
            setCustomers(prev => prev.filter(customer => customer._id !== id));
        } catch (err) {
            console.error('Failed to delete customer', err);
        }
    };

    // Filter customers based on search
    const filteredData = customers.filter(item => {
        const searchText = [item.name, item.email, item.subscription_status, item.status]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return searchText.includes(search.toLowerCase());
    });

    // Get counts for stats
    const totalCustomers = customers.length;
    const activeCustomers = customers.filter(c => c.status === 'active').length;
    const inactiveCustomers = customers.filter(c => c.status !== 'active').length;
    const activeSubscriptions = customers.filter(c => c.subscription_status === 'active').length;

    return (
        <div className={`customers-container ${darkMode ? 'dark' : ''}`}>
            <div className="customers-content">
                {/* Page Header */}
                <PageHeader
                    title="Customers"
                    buttonText="Add Customer"
                    onAdd={() => setShow(true)}
                />

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Customers</span>
                            <span className="stat-value">{totalCustomers}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon active">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Active Customers</span>
                            <span className="stat-value">{activeCustomers}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon inactive">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Inactive Customers</span>
                            <span className="stat-value">{inactiveCustomers}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon subscription">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                            </svg>
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Active Subscriptions</span>
                            <span className="stat-value">{activeSubscriptions}</span>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="table-card-modern">
                    {/* Search and Info */}
                    <div className="table-toolbar">
                        <div className="search-wrapper">
                            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input
                                className="search-input-modern"
                                placeholder="Search customers by name, email, or status..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="table-info">
                            Showing {filteredData.length} of {customers.length} customers
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-wrapper-modern">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading customers...</p>
                            </div>
                        ) : (
                            <CustomDataTable
                                columns={columns}
                                data={filteredData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            <AppModal
                show={show}
                title={selectedCustomer ? "Edit Customer" : "Add Customer"}
                onClose={() => {
                    setShow(false);
                    setSelectedCustomer(null);
                }}
            />

            {/* View Modal */}
            {showViewModal && selectedCustomer && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Customer Details</h3>
                            <button className="modal-close-btn" onClick={() => setShowViewModal(false)}>
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="customer-detail-row">
                                <span className="detail-label">Name</span>
                                <span className="detail-value">{selectedCustomer.name || "-"}</span>
                            </div>
                            <div className="customer-detail-row">
                                <span className="detail-label">Email</span>
                                <span className="detail-value">{selectedCustomer.email || "-"}</span>
                            </div>
                            <div className="customer-detail-row">
                                <span className="detail-label">Subscription Status</span>
                                <span className={`status-badge ${selectedCustomer.subscription_status === 'active' ? 'active' : 'inactive'}`}>
                                    {selectedCustomer.subscription_label || (selectedCustomer.subscription_status === 'active' ? 'Active' : 'Inactive')}
                                </span>
                            </div>
                            <div className="customer-detail-row">
                                <span className="detail-label">Status</span>
                                <span className={`status-badge ${selectedCustomer.status === 'active' ? 'active' : 'inactive'}`}>
                                    {selectedCustomer.status === 'active' ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-cancel-btn" onClick={() => setShowViewModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}